<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>カスタマーランキング</span>
      <div class="d-flex align-center flex-wrap">
        <v-select
          v-model="salesScope"
          :items="salesScopeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="min-width: 128px; max-width: 136px;"
          class="mr-2"
          @change="handleSalesScopeChange"
        />
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="min-width: 112px; max-width: 120px;"
          class="mr-2"
          @change="handleTypeChange"
        />
        <v-select
          v-model="selectedLimit"
          :items="limitOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="min-width: 96px; max-width: 100px;"
          class="mr-2"
          @change="handleLimitChange"
        />
        <v-btn
          :disabled="isLoading || ranking.length === 0"
          :loading="downloadingCsv"
          color="secondary"
          small
          outlined
          @click="downloadCsv"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiDownload }}
          </v-icon>
          CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-subtitle class="pb-0">
      <div class="text-caption text--secondary">
        <div>全ての売上: 期間内に来店した卓の売上（値引き・手数料調整後の大計）を集計します</div>
        <div>回収済みのみ: 通常支払い分（来店日基準）と期間内に回収した売掛金（回収日基準）を集計します。来店のない期間でも回収した売掛金は計上されます</div>
      </div>
    </v-card-subtitle>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <v-simple-table
        v-else-if="ranking.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th
                class="text-center"
                style="width: 64px; white-space: nowrap;"
              >
                順位
              </th>
              <th>顧客名</th>
              <th class="text-right">
                大計売上
              </th>
              <th class="text-right">
                来店回数
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in ranking"
              :key="item.customerId"
              class="cursor-pointer"
              @click="handleRowClick(item)"
            >
              <td class="text-center">
                <v-chip
                  v-if="item.rank <= 3"
                  :color="getRankColor(item.rank)"
                  small
                  text-color="white"
                >
                  {{ item.rank }}
                </v-chip>
                <span v-else>{{ item.rank }}</span>
              </td>
              <td>{{ item.customerName }}</td>
              <td class="text-right">
                {{ formatCurrency(item.totalAmountWithTax) }}
              </td>
              <td class="text-right text-caption text--secondary">
                {{ item.visitCount }}回
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div
        v-else
        class="text-center py-8 grey--text"
      >
        データがありません
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { watch, computed } from '@vue/composition-api'
import { mdiDownload } from '@mdi/js'
import { formatCurrency } from '@/utils/formatters'
import { downloadBlob } from '@/utils/csv'
import useCustomerRanking, { RANKING_TYPES, LIMIT_OPTIONS, SALES_SCOPE_OPTIONS } from '../composables/useCustomerRanking'

// 日付を YYYY-MM-DD 文字列に変換（Date / 文字列どちらも対応）
const toDateStr = date => {
  if (!date) return ''
  if (typeof date === 'string') return date.slice(0, 10)

  return date.toISOString().slice(0, 10)
}

export default {
  name: 'CustomerRanking',
  props: {
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const {
      ranking,
      loading,
      downloadingCsv,
      selectedType,
      selectedLimit,
      // 売上対象フィルター: collected_only時は売掛未回収分を除いた大計（回収済み+売掛以外の支払分）で集計する
      salesScope,
      fetchRanking,
      fetchRankingCsv,
    } = useCustomerRanking(root)

    const isLoading = computed(() => loading.value)

    const typeOptions = RANKING_TYPES
    const limitOptions = LIMIT_OPTIONS
    const salesScopeOptions = SALES_SCOPE_OPTIONS

    const getRankColor = rank => {
      if (rank === 1) return '#D4A017'
      if (rank === 2) return '#7B8794'
      if (rank === 3) return '#A0522D'

      return 'grey'
    }

    const loadRanking = () => {
      fetchRanking({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
        salesScope: salesScope.value,
      })
    }

    const handleTypeChange = () => {
      loadRanking()
    }

    const handleLimitChange = () => {
      loadRanking()
    }

    const handleSalesScopeChange = () => {
      loadRanking()
    }

    const handleRowClick = item => {
      emit('customer-click', item.customerId)
    }

    // ランキングCSV（大計＝リアルトータル・合計行付き）をAPIから取得してダウンロードする
    const downloadCsv = async () => {
      const blob = await fetchRankingCsv({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
        salesScope: salesScope.value,
      })
      if (!blob) return

      const period = [toDateStr(props.startDate), toDateStr(props.endDate)].filter(Boolean).join('_')
      const fileName = `customer_ranking${period ? `_${period}` : ''}.csv`

      downloadBlob({ blob, fileName })
    }

    // 日付が変更されたらリロード
    watch(
      () => [props.startDate, props.endDate],
      () => {
        loadRanking()
      },
      { immediate: true },
    )

    return {
      ranking,
      isLoading,
      downloadingCsv,
      selectedType,
      selectedLimit,
      salesScope,
      typeOptions,
      limitOptions,
      salesScopeOptions,
      formatCurrency,
      getRankColor,
      handleTypeChange,
      handleLimitChange,
      handleSalesScopeChange,
      handleRowClick,
      downloadCsv,
      icons: { mdiDownload },
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
